import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AlertTriangle, CheckCircle2, Loader2, Send, X, ImagePlus } from 'lucide-react';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useLocation } from 'react-router-dom';
import { APP_VERSION, checkedImage, feedbackApi, feedbackError, TYPES } from './api';

export const FeedbackDialog = ({ open, onOpenChange, account }) => {
  const location = useLocation();
  const [kind, setKind] = useState('bug');
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const length = Array.from(text.trim()).length;
  useEffect(() => {
    if (!open) { setText(''); setImage(null); setError(''); setSuccess(false); setKind('bug'); }
  }, [open]);
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const url = URL.createObjectURL(image.file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  const chooseImage = async event => {
    const files = [...event.target.files]; event.target.value = '';
    if (!files.length) return;
    if (files.length > 1) { setError('Зөвхөн нэг зураг сонгоно уу.'); return; }
    setChecking(true); setError('');
    try { const ext = await checkedImage(files[0]); setImage({ file: files[0], ext }); }
    catch (e) { setError(e.message); setImage(null); }
    finally { setChecking(false); }
  };
  const submit = async event => {
    event.preventDefault();
    if (submitting.current || busy || checking) return;
    if (length < 10) { setError('Дор хаяж 10 тэмдэгт бичнэ үү.'); return; }
    if (length > 2000) { setError('Текст 2000 тэмдэгтээс ихгүй байна.'); return; }
    submitting.current = true;
    setBusy(true); setError('');
    const form = new FormData();
    form.append('kind', kind); form.append('text', text.trim());
    form.append('page_path', location.pathname); form.append('app_version', APP_VERSION);
    if (image) form.append('image', image.file, `screenshot.${image.ext}`);
    try { await feedbackApi.send(form); setSuccess(true); setText(''); setImage(null); }
    catch (e) { setError(feedbackError(e)); }
    finally { submitting.current = false; setBusy(false); }
  };
  return <Dialog open={open} onOpenChange={value => { if (!busy && !checking) onOpenChange(value); }}>
    <DialogPortal>
      <DialogOverlay className="feedback-dialog-overlay" />
      <DialogPrimitive.Content className="feedback-dialog-shell" data-testid="feedback-dialog-modal">
        <DialogTitle className="feedback-dialog-title">Санал хүсэлт</DialogTitle>
        <DialogDescription className="sr-only">RENTOS санал хүсэлтийн маягт</DialogDescription>
        <button type="button" className="feedback-icon feedback-close" onClick={() => onOpenChange(false)}
          disabled={busy || checking} aria-label="Хаах" title="Хаах" data-testid="feedback-dialog-close"><X size={20} /></button>
        {success ? <div className="feedback-success" role="status" data-testid="feedback-success-alert">
          <CheckCircle2 size={40} aria-hidden="true" /><p>Баярлалаа, таны санал хүлээн авлаа</p>
          <Button data-testid="feedback-success-close" onClick={() => onOpenChange(false)}>Хаах</Button>
        </div> : <form onSubmit={submit} className="feedback-form">
          <p className="feedback-warning" data-testid="feedback-privacy-warning"><AlertTriangle size={18} aria-hidden="true" />
            Нууц үг, картын дугаар, банкны мэдээлэл бүү бичээрэй.</p>
          <fieldset><legend>Төрөл</legend><div className="feedback-type-options">
            {Object.entries(TYPES).map(([value, label]) => <label key={value} className={kind === value ? 'selected' : ''}>
              <input type="radio" name="feedback-kind" value={value} checked={kind === value} disabled={busy}
                onChange={() => setKind(value)} data-testid={`feedback-type-${value}`} />{label}</label>)}
          </div></fieldset>
          <div><label htmlFor="feedback-text">Текст</label>
            <Textarea id="feedback-text" data-testid="feedback-text-textarea" value={text} disabled={busy}
              onChange={event => setText(Array.from(event.target.value).slice(0, 2000).join(''))}
              rows={5} aria-describedby="feedback-length" placeholder="Таны санал, алдаа эсвэл асуулт…" required />
            <p id="feedback-length" data-testid="feedback-text-char-count" className="feedback-caption">{length}/2000 · хамгийн багадаа 10 тэмдэгт</p>
          </div>
          <div><label className="feedback-upload" htmlFor="feedback-image-input"><ImagePlus size={20} aria-hidden="true" />Дэлгэцийн зураг · PNG/JPG, ≤2MB
              <input id="feedback-image-input" data-testid="feedback-image-input" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                disabled={busy || checking} onChange={chooseImage} aria-label="Дэлгэцийн зураг сонгох" /></label>
            {checking && <p role="status" data-testid="feedback-image-checking">Зургийг шалгаж байна…</p>}
            {preview && <div className="feedback-image-preview-box"><img src={preview} alt="Сонгосон дэлгэцийн зураг" data-testid="feedback-image-preview" />
              <button type="button" data-testid="feedback-image-remove-btn" className="feedback-icon" disabled={busy}
                title="Зураг хасах" aria-label="Зураг хасах" onClick={() => setImage(null)}><X size={18} /></button></div>}
            <p className="feedback-caption" data-testid="feedback-image-warning">Зураг сонголттой. Зураг дахь нууц мэдээллийг өөрөө шалгаарай.</p>
          </div>
          <div className="feedback-meta" data-testid="feedback-meta-summary">
            <span data-testid="feedback-meta-club">{account.club_name}</span><span data-testid="feedback-meta-role">{account.role}</span>
            <span data-testid="feedback-meta-version">v{APP_VERSION}</span><span data-testid="feedback-meta-browser">{account.browser}</span>
          </div>
          {error && <p role="alert" className="feedback-error" data-testid="feedback-submit-error">{error}</p>}
          <Button type="submit" data-testid="feedback-submit-btn" disabled={busy || checking} className="feedback-submit">
            {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}{busy ? 'Илгээж байна…' : 'Илгээх'}
          </Button>
        </form>}
      </DialogPrimitive.Content>
    </DialogPortal>
  </Dialog>;
};